import { Icon } from '@campgladiator/cgui-core.atoms.icon'
import { Loader } from '@campgladiator/cgui-core.atoms.loader'
import { Paragraph } from '@campgladiator/cgui-core.atoms.typography'
import { PreferenceCard } from '../preference-card'
import { PreferenceTile } from '../preference-tile'
import styles from './communication-preferences-panel.module.scss'

type Trainer = {
  firstName?: string
  lastName?: string
  photo?: string
}

type CommunicationPreferencesPanelProps = {
  allTrainersMessagesOptIn: boolean
  isLoading: boolean
  isSaving: boolean
  marketingSmsOptIn: boolean
  setAllTrainersMessagesOptIn: (value: boolean) => void
  setMarketingSmsOptIn: (value: boolean) => void
  setTransactionalSmsOptIn: (value: boolean) => void
  setYourTrainerMessagesOptIn: (value: boolean) => void
  trainer: Trainer | null
  transactionalSmsOptIn: boolean
  yourTrainerMessagesOptIn: boolean
}

const CommunicationPreferencesPanel = ({
  allTrainersMessagesOptIn,
  isLoading,
  isSaving,
  marketingSmsOptIn,
  setAllTrainersMessagesOptIn,
  setMarketingSmsOptIn,
  setTransactionalSmsOptIn,
  setYourTrainerMessagesOptIn,
  trainer,
  transactionalSmsOptIn,
  yourTrainerMessagesOptIn,
}: CommunicationPreferencesPanelProps) => {
  const trainerDisplayName =
    trainer?.firstName || trainer?.lastName
      ? `${trainer?.firstName ?? ''} ${trainer?.lastName ?? ''}`.trim()
      : 'Servicing Trainer'

  if (isLoading) {
    return (
      <div className={styles.loaderWrap}>
        <Loader />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <Paragraph size="small" weight="book" className={styles.description}>
        Manage how the camper receives notifications across different channels.
      </Paragraph>

      <PreferenceCard
        title="Marketing Communications"
        description="Updates on new programs, special offers, and newsletters."
      >
        <div className={styles.emailTile}>
          <div className={styles.tileLeft}>
            <Icon.Line name="icon-envelope" />

            <div>
              <Paragraph size="default">Email</Paragraph>

              <Paragraph size="small" weight="book" className={styles.hint}>
                Contact support to update email marketing preferences.
              </Paragraph>
            </div>
          </div>
        </div>

        <PreferenceTile
          icon="icon-comment-alt-lines"
          label="SMS"
          checked={marketingSmsOptIn}
          disabled={isSaving}
          onChange={setMarketingSmsOptIn}
        />
      </PreferenceCard>

      <PreferenceCard
        title="Account & Billing"
        description="Payment and account security alerts. Required for Security."
      >
        <PreferenceTile
          icon="icon-envelope"
          label="Email"
          checked
          disabled
          onChange={() => {}}
        />
      </PreferenceCard>

      <PreferenceCard
        title="Transactional"
        description="Rate workout prompts, tip trainer reminders, and attendance confirmations."
      >
        <PreferenceTile
          icon="icon-comment-alt-lines"
          label="SMS"
          checked={transactionalSmsOptIn}
          disabled={isSaving}
          onChange={setTransactionalSmsOptIn}
        />
      </PreferenceCard>

      <PreferenceCard
        title="Trainer Messaging"
        description="Direct messages from servicing trainer and other trainers."
      >
        <PreferenceTile
          avatar={trainer?.photo}
          label="Servicing Trainer"
          subLabel={trainerDisplayName}
          isSolid
          checked={yourTrainerMessagesOptIn}
          disabled={isSaving}
          onChange={setYourTrainerMessagesOptIn}
        />

        <PreferenceTile
          label="Allow Messages from All Trainers"
          checked={allTrainersMessagesOptIn}
          disabled={isSaving}
          onChange={setAllTrainersMessagesOptIn}
        />
      </PreferenceCard>
    </div>
  )
}

export default CommunicationPreferencesPanel
